import moment from "moment";
import { GitBranch } from "lucide-react";
import { formatarValorEmRealBR } from "@/components/utils/Utils";

type RateioItem = {
  competencia: string;
  valor: number;
};

function parseMoney(value: any) {
  if (typeof value === "number") return value;
  if (value === null || value === undefined) return 0;

  const raw = String(value).replace(/[^\d,.-]/g, "");

  if (raw.includes(",")) {
    return Number(raw.replace(/\./g, "").replace(",", ".")) || 0;
  }

  return Number(raw) || 0;
}

function normalizeCompetencia(value: any) {
  if (!value) return "";
  const raw = String(value);
  const match = raw.match(/^(\d{4})-(\d{2})/);

  if (match) return `${match[1]}-${match[2]}`;

  const parsed = moment.utc(raw);
  return parsed.isValid() ? parsed.format("YYYY-MM") : raw;
}

export function getDeskRateioItems(desk: any): RateioItem[] {
  const rows =
    desk?.monthlyRevenues ??
    desk?.monthly_revenues ??
    desk?.rateio_mensal_items ??
    [];

  if (!Array.isArray(rows)) return [];

  return rows
    .map((item) => ({
      competencia: normalizeCompetencia(item?.competencia),
      valor: parseMoney(item?.valor ?? item?.value ?? item?.amount),
    }))
    .filter((item) => item.competencia);
}

export function isDeskRateada(desk: any) {
  return !!desk?.rateio_mensal_enabled || getDeskRateioItems(desk).length > 0;
}

function getValorCompetencia(desk: any, items: RateioItem[]) {
  if (
    desk?.forecast_valor_proposta !== null &&
    desk?.forecast_valor_proposta !== undefined
  ) {
    return parseMoney(desk.forecast_valor_proposta);
  }

  const currentCompetencia = moment().format("YYYY-MM");
  return items.find((item) => item.competencia === currentCompetencia)?.valor;
}

function monthLabel(competencia: string) {
  return moment.utc(`${competencia}-01`).format("MMM/YYYY");
}

export function DeskRateioBadge({ compact = false }: { compact?: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-azul-100 px-2 py-0.5 text-[10px] font-semibold text-azul-400"
      title="Desk com valor rateado por competência mensal"
    >
      <GitBranch size={11} />
      {compact ? "Rateado" : "Rateado por competência mensal"}
    </span>
  );
}

export function DeskRateioResumo({
  desk,
  className = "",
  compact = false,
}: {
  desk: any;
  className?: string;
  compact?: boolean;
}) {
  if (!isDeskRateada(desk)) return null;

  const items = getDeskRateioItems(desk);
  const valorCompetencia = getValorCompetencia(desk, items);
  const totalRateado = items.reduce((total, item) => total + item.valor, 0);

  return (
    <div
      className={`rounded-md border border-azul-400/20 bg-azul-400/5 p-3 text-sm text-cinza-800 ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <DeskRateioBadge compact={compact} />
        {items.length > 0 && (
          <span className="text-xs font-medium text-cinza-500">
            Soma rateada: {formatarValorEmRealBR(totalRateado)}
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {valorCompetencia !== undefined && (
          <div className="rounded bg-white px-3 py-2">
            <p className="text-xs text-cinza-400">Valor da competência</p>
            <p className="font-semibold text-azul-400">
              {formatarValorEmRealBR(valorCompetencia)}
            </p>
          </div>
        )}
        {desk?.valor_proposta && desk.valor_proposta !== "0.00" && (
          <div className="rounded bg-white px-3 py-2">
            <p className="text-xs text-cinza-400">Valor total da proposta</p>
            <p className="font-semibold text-cinza-800">
              {formatarValorEmRealBR(desk.valor_proposta)}
            </p>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="mt-3 max-h-48 overflow-y-auto rounded bg-white">
          <table className="w-full text-xs">
            <thead className="bg-cinza-100 text-cinza-400">
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  Competência
                </th>
                <th className="px-3 py-2 text-right font-medium">Valor</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.competencia} className="border-t border-cinza-100">
                  <td className="px-3 py-2">{monthLabel(item.competencia)}</td>
                  <td className="px-3 py-2 text-right font-medium text-azul-400">
                    {formatarValorEmRealBR(item.valor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
